import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_USER_PROFILE } from '../../graphql';
import { loadProfilePhoto, saveProfilePhoto, uploadProfilePhoto } from '../../lib/profilePhoto';
import { Loader } from '../common/PageLoader';

interface UserProfileData {
  me?: {
    id: string;
    email: string;
  };
}

const NOTIFICATIONS = [
  ['ask', 'An agent asks for approval', 'Email and in-app, as it happens'],
  ['digest', 'Daily digest', 'What agents published and changed, every morning at 8 AM'],
  ['fail', 'A publish is blocked', 'When checks stop a scheduled publish'],
] as const;

export function Profile() {
  const { loading, error, data } = useQuery<UserProfileData>(GET_USER_PROFILE);
  const email = data?.me?.email || '';
  const userId = data?.me?.id || '';
  const [name, setName] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [passwordNote, setPasswordNote] = useState(false);
  const [notifs, setNotifs] = useState({ ask: true, digest: true, fail: true });

  useEffect(() => {
    if (email) setName(email.split('@')[0]);
  }, [email]);

  useEffect(() => {
    if (userId) setPhotoUrl(loadProfilePhoto(userId));
  }, [userId]);

  const initials = (name || email || 'ME').slice(0, 2).toUpperCase();

  if (loading) return <Loader label="Loading" className="ink-lede" />;
  if (error) return <p className="ink-lede">Could not load your profile.</p>;

  return (
    <div>
      <h1 className="ink-h1">Profile</h1>
      <p className="ink-lede">Your sign-in and how TinyCMS reaches you.</p>

      <div className="ink-profile-photo">
        <span className="ink-profile-avatar">
          {photoUrl ? <img src={photoUrl} alt="" /> : initials}
        </span>
        <label className="ink-bill-link" style={{ cursor: 'pointer' }}>
          Change photo
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            hidden
            disabled={uploading || !userId}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file || !userId) return;
              const preview = URL.createObjectURL(file);
              setPhotoUrl(preview);
              setPhotoError('');
              setUploading(true);
              void uploadProfilePhoto(file)
                .then((url) => {
                  saveProfilePhoto(userId, url);
                  setPhotoUrl(url);
                })
                .catch((err: unknown) => {
                  setPhotoUrl(loadProfilePhoto(userId));
                  setPhotoError(err instanceof Error ? err.message : 'Could not save that photo.');
                })
                .finally(() => {
                  URL.revokeObjectURL(preview);
                  setUploading(false);
                });
            }}
          />
        </label>
        {uploading ? <span className="ink-team-email">Saving photo…</span> : null}
        {photoError ? <span className="ink-team-email">{photoError}</span> : null}
      </div>

      <div className="ink-profile-grid">
        <label>
          Name
          <input
            className="ink-acct-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          Email
          <input className="ink-acct-input" type="email" value={email} readOnly />
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ font: '500 13px/1.3 var(--ink-font)' }}>Password</span>
          <button
            type="button"
            className="ink-btn"
            style={{ width: 'max-content' }}
            onClick={() => setPasswordNote(true)}
          >
            Change password
          </button>
          {passwordNote ? (
            <span className="ink-team-email">
              Password changes are not available from this screen yet.
            </span>
          ) : null}
        </div>
      </div>

      <h2 className="ink-bill-h">Notifications</h2>
      <div className="ink-profile-notifs">
        {NOTIFICATIONS.map(([key, label, desc]) => (
          <button
            key={key}
            type="button"
            className="ink-profile-notif"
            onClick={() => setNotifs((prev) => ({ ...prev, [key]: !prev[key] }))}
          >
            <span style={{ flex: 1 }}>
              <span className="ink-profile-notif-name">{label}</span>
              <span className="ink-profile-notif-desc">{desc}</span>
            </span>
            <span className="ink-profile-switch" data-on={notifs[key] ? 'true' : 'false'} />
          </button>
        ))}
      </div>
    </div>
  );
}
